import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  browseDocs,
  createDocFolder,
  updateDocFolder,
  deleteDocFolder,
  uploadDocFile,
  replaceDocFile,
  updateDocFile,
  deleteDocFile,
  docFileUrl,
  docPreviewUrl,
  folderZipUrl,
  downloadDocsZip,
  getDocFolderTree,
  listDocVersions,
  listDocTrash,
  listMyDocs,
  listRecentDocs,
  listStarredDocs,
  locateDocFile,
  restoreDocVersion,
  searchDocs,
  setDocStar,
  docVersionUrl,
  canEditDoc,
  officeEditorFor,
  copyDocItems,
  getDocUnlockToken,
  unlockDoc,
  ApiError,
  type DocFile,
  type DocFolder,
  type DocVersion,
} from "@/lib/api";
import { Modal } from "@/components/Modal";
import { DocPreviewModal } from "@/components/DocPreviewModal";
import { DocEditorModal } from "@/components/DocEditorModal";
import { OfficeEditorModal } from "@/components/OfficeEditorModal";
import { ContextMenu, type ContextMenuState, type MenuItem } from "@/components/docs/ContextMenu";
import { DocsSidebar, type DocScope } from "@/components/docs/DocsSidebar";
import { ResultsList, type ListedItem } from "@/components/docs/ResultsList";
import { TrashModal } from "@/components/docs/TrashModal";
import { MarqueeBox, useMarquee } from "@/components/docs/useMarquee";
import {
  EmptyState,
  SkeletonRows,
  fileIcon,
  fmtDateTime,
  fmtSize,
  rowKey,
  suffixName,
  type MoveItem,
  type SortDir,
  type SortKey,
} from "@/components/docs/shared";

type ConflictPolicy = "replace" | "keep" | "skip";
type UploadItem = { file: File; relPath?: string; replaceId?: string };

/** Uniform row model so one cursor/selection works across every listing. */
type Row =
  | { kind: "folder"; id: string; name: string; starred: boolean; folder: DocFolder }
  | { kind: "file"; id: string; name: string; starred: boolean; file: DocFile };

/** "3 items" / "“report.pdf”" — for toast and menu wording. */
const label = (items: { name: string }[]) =>
  items.length === 1 ? `“${items[0].name}”` : `${items.length} items`;

/** MIME type marking an in-app drag, so external file drops stay uploads. */
const DRAG_TYPE = "application/x-vernias-docs";

const VIEW_SCOPES = ["recent", "starred", "mine"] as const;
type ViewScope = (typeof VIEW_SCOPES)[number];

/* ════════════════════════════════════════════════════════════════════════
   Page
   ════════════════════════════════════════════════════════════════════════ */
export function DocsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ folderId?: string; section?: string }>();
  const [search, setSearchParams] = useSearchParams();

  // ── Scope + deep links come from the URL, so every view is linkable ──────
  const scope: DocScope = useMemo(() => {
    const section = params.section;
    if (section && (VIEW_SCOPES as readonly string[]).includes(section)) {
      return { kind: section as ViewScope };
    }
    return { kind: "folder", folderId: params.folderId ?? null };
  }, [params.folderId, params.section]);

  const query = search.get("q") ?? "";
  const deepLinkFileId = search.get("file");
  const folderId = scope.kind === "folder" ? scope.folderId : null;

  const setScope = useCallback(
    (s: DocScope) => {
      const qs = query ? `?q=${encodeURIComponent(query)}` : "";
      if (s.kind === "folder") {
        navigate(s.folderId ? `/docs/folder/${s.folderId}${qs}` : `/docs${qs}`);
      } else {
        navigate(`/docs/${s.kind}${qs}`);
      }
    },
    [navigate, query],
  );

  const goToFolder = useCallback(
    (id: string | null) => setScope({ kind: "folder", folderId: id }),
    [setScope],
  );

  const setQuery = useCallback(
    (q: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (q) next.set("q", q);
          else next.delete("q");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Search input is local so typing stays responsive; the URL lags behind it.
  const [queryDraft, setQueryDraft] = useState(query);
  useEffect(() => setQueryDraft(query), [query]);
  useEffect(() => {
    if (queryDraft === query) return;
    const t = setTimeout(() => setQuery(queryDraft), 250);
    return () => clearTimeout(t);
  }, [queryDraft, query, setQuery]);

  const [view, setView] = useState<"list" | "grid">("list");
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "name",
    dir: "asc",
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cursor, setCursor] = useState(-1);
  /** Anchor for shift-click / shift-arrow range selection. */
  const anchorRef = useRef<number>(-1);

  const [preview, setPreview] = useState<DocFile | null>(null);
  const [editing, setEditing] = useState<DocFile | null>(null);
  const [history, setHistory] = useState<DocFile | null>(null);
  const [clipboard, setClipboard] = useState<{
    mode: "cut" | "copy";
    items: MoveItem[];
  } | null>(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const [conflict, setConflict] = useState<{
    names: string[];
    files: UploadItem[];
  } | null>(null);
  const [renaming, setRenaming] = useState<
    | { kind: "folder"; item: DocFolder }
    | { kind: "file"; item: DocFile }
    | null
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<
    | { kind: "folder"; item: DocFolder }
    | { kind: "file"; item: DocFile }
    | { kind: "bulk"; items: MoveItem[] }
    | null
  >(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  /** Folder row currently under an in-app drag. */
  const [dropRow, setDropRow] = useState<string | null>(null);
  const [uploads, setUploads] = useState<{ name: string; status: "up" | "ok" | "err" }[]>([]);
  const [toast, setToast] = useState<{ text: string; undo?: () => void } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset selection and cursor whenever the listing changes underneath them.
  useEffect(() => {
    setSelected(new Set());
    setCursor(-1);
    anchorRef.current = -1;
  }, [folderId, scope.kind, query]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Data ────────────────────────────────────────────────────────────────
  const searching = query.trim().length > 0;

  const browse = useQuery({
    queryKey: ["docs", folderId],
    queryFn: () => browseDocs(folderId),
    enabled: scope.kind === "folder" && !searching,
  });
  const results = useQuery({
    queryKey: ["docs-search", query],
    queryFn: () => searchDocs(query),
    enabled: searching,
  });
  const recent = useQuery({
    queryKey: ["docs-recent"],
    queryFn: listRecentDocs,
    enabled: scope.kind === "recent" && !searching,
  });
  const starred = useQuery({
    queryKey: ["docs-starred"],
    queryFn: listStarredDocs,
    enabled: scope.kind === "starred" && !searching,
  });
  const mine = useQuery({
    queryKey: ["docs-mine"],
    queryFn: listMyDocs,
    enabled: scope.kind === "mine" && !searching,
  });
  const tree = useQuery({ queryKey: ["docs-folder-tree"], queryFn: getDocFolderTree });
  const trash = useQuery({ queryKey: ["docs-trash"], queryFn: listDocTrash });

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["docs"] });
    void qc.invalidateQueries({ queryKey: ["docs-search"] });
    void qc.invalidateQueries({ queryKey: ["docs-recent"] });
    void qc.invalidateQueries({ queryKey: ["docs-starred"] });
    void qc.invalidateQueries({ queryKey: ["docs-mine"] });
    void qc.invalidateQueries({ queryKey: ["docs-folder-tree"] });
    void qc.invalidateQueries({ queryKey: ["docs-trash"] });
  }, [qc]);

  // ── Deep link: ?file=<id> opens that file, navigating to its folder ──────
  const handledDeepLink = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkFileId || handledDeepLink.current === deepLinkFileId) return;
    handledDeepLink.current = deepLinkFileId;
    void (async () => {
      try {
        const { folderId: home } = await locateDocFile(deepLinkFileId);
        const data = await browseDocs(home);
        const doc = data.documents.find((d) => d.id === deepLinkFileId);
        // Drop ?file= so a refresh doesn't reopen the preview forever.
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("file");
            return next;
          },
          { replace: true },
        );
        if (home !== folderId) goToFolder(home);
        if (doc) openFile(doc);
      } catch {
        setToast({ text: "That file no longer exists." });
      }
    })();
    // openFile/goToFolder are stable enough for a one-shot deep link resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkFileId]);

  // ── Uploads (with conflict handling) ────────────────────────────────────
  async function processUpload(items: UploadItem[]) {
    setUploads(items.map((f) => ({ name: f.file.name, status: "up" as const })));
    for (const { file, relPath, replaceId } of items) {
      try {
        if (replaceId) await replaceDocFile(replaceId, file);
        else await uploadDocFile(file, folderId, relPath);
        setUploads((u) =>
          u.map((x) => (x.name === file.name ? { ...x, status: "ok" as const } : x)),
        );
      } catch {
        setUploads((u) =>
          u.map((x) => (x.name === file.name ? { ...x, status: "err" as const } : x)),
        );
      }
    }
    refresh();
    setTimeout(() => setUploads([]), 3500);
  }

  /** Detect same-name collisions in the current folder before uploading. */
  function startUpload(items: UploadItem[]) {
    const existing = new Map(
      (browse.data?.documents ?? []).map((d) => [d.name.toLowerCase(), d.id]),
    );
    // Only files landing directly in this folder (no relPath) can collide here.
    const collisions = items.filter((it) => !it.relPath && existing.has(it.file.name.toLowerCase()));
    if (collisions.length) {
      setConflict({ names: collisions.map((c) => c.file.name), files: items });
    } else {
      void processUpload(items);
    }
  }

  function resolveConflict(policy: ConflictPolicy) {
    if (!conflict) return;
    const existing = new Map(
      (browse.data?.documents ?? []).map((d) => [d.name.toLowerCase(), d.id]),
    );
    const items: UploadItem[] = [];
    for (const it of conflict.files) {
      const hit = !it.relPath && existing.get(it.file.name.toLowerCase());
      if (!hit) {
        items.push(it);
        continue;
      }
      if (policy === "skip") continue;
      if (policy === "replace") items.push({ ...it, replaceId: hit });
      else {
        // keep both — upload under a suffixed name
        const renamed = new File([it.file], suffixName(it.file.name, 1), {
          type: it.file.type,
        });
        items.push({ file: renamed });
      }
    }
    setConflict(null);
    if (items.length) void processUpload(items);
  }

  /** Directory-aware drop: walks dropped folders and preserves their structure. */
  async function onDrop(e: React.DragEvent) {
    // In-app row drags are moves, handled by the row/tree drop targets.
    if (e.dataTransfer.types.includes(DRAG_TYPE)) return;
    e.preventDefault();
    setDragOver(false);
    const items = Array.from(e.dataTransfer.items);
    const collected: UploadItem[] = [];

    async function walk(entry: FileSystemEntry, path: string): Promise<void> {
      if (entry.isFile) {
        const file = await new Promise<File>((res, rej) =>
          (entry as FileSystemFileEntry).file(res, rej),
        );
        collected.push({ file, relPath: path || undefined });
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        for (;;) {
          const batch = await new Promise<FileSystemEntry[]>((res, rej) =>
            reader.readEntries(res, rej),
          );
          if (!batch.length) break;
          for (const child of batch) {
            await walk(child, path ? `${path}/${entry.name}` : entry.name);
          }
        }
      }
    }

    const entries = items
      .map((i) => i.webkitGetAsEntry?.())
      .filter((x): x is FileSystemEntry => !!x);
    if (entries.length) {
      for (const entry of entries) await walk(entry, "");
    } else {
      for (const f of Array.from(e.dataTransfer.files)) collected.push({ file: f });
    }
    if (collected.length) startUpload(collected);
  }

  // ── Rows: one ordered list per scope, feeding cursor + selection ─────────
  const listed: ListedItem[] | null = searching
    ? (results.data?.results ?? [])
    : scope.kind === "recent"
      ? (recent.data ?? [])
      : scope.kind === "starred"
        ? (starred.data ?? [])
        : scope.kind === "mine"
          ? (mine.data ?? [])
          : null;

  const { folders, documents } = useMemo(() => {
    if (listed) return { folders: [] as DocFolder[], documents: [] as DocFile[] };
    const fs = [...(browse.data?.folders ?? [])];
    const ds = [...(browse.data?.documents ?? [])];
    const mul = sort.dir === "asc" ? 1 : -1;
    const byName = (a: string, b: string) => a.localeCompare(b) * mul;
    const byDate = (a: string, b: string) =>
      (new Date(a).getTime() - new Date(b).getTime()) * mul;
    fs.sort((a, b) => {
      switch (sort.key) {
        case "size":
          return (a.itemCount - b.itemCount) * mul;
        case "added":
          return byDate(a.createdAt, b.createdAt);
        case "edited":
          return byDate(a.updatedAt, b.updatedAt);
        default:
          return byName(a.name, b.name);
      }
    });
    ds.sort((a, b) => {
      switch (sort.key) {
        case "size":
          return (a.size - b.size) * mul;
        case "added":
          return byDate(a.createdAt, b.createdAt);
        case "edited":
          return byDate(a.updatedAt, b.updatedAt);
        default:
          return byName(a.name, b.name);
      }
    });
    return { folders: fs, documents: ds };
  }, [browse.data, listed, sort]);

  const rows: Row[] = useMemo(() => {
    if (listed) {
      return listed.map((item) =>
        item.kind === "folder"
          ? { kind: "folder", id: item.id, name: item.name, starred: item.starred, folder: item }
          : { kind: "file", id: item.id, name: item.name, starred: item.starred, file: item },
      );
    }
    return [
      ...folders.map((f) => ({
        kind: "folder" as const,
        id: f.id,
        name: f.name,
        starred: f.starred,
        folder: f,
      })),
      ...documents.map((d) => ({
        kind: "file" as const,
        id: d.id,
        name: d.name,
        starred: d.starred,
        file: d,
      })),
    ];
  }, [listed, folders, documents]);

  const activeQuery = searching
    ? results
    : scope.kind === "recent"
      ? recent
      : scope.kind === "starred"
        ? starred
        : scope.kind === "mine"
          ? mine
          : browse;
  const isLoading = activeQuery.isLoading;
  const empty = rows.length === 0;

  // ── Selection ───────────────────────────────────────────────────────────
  const toggleSel = (key: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const selectedItems: MoveItem[] = rows
    .filter((r) => selected.has(rowKey(r.kind, r.id)))
    .map((r) => ({ kind: r.kind, id: r.id, name: r.name }));

  const allKeys = rows.map((r) => rowKey(r.kind, r.id));

  /** Rubber-band selection: drag from empty space, exactly like Finder. */
  const marqueeBase = useRef<Set<string>>(new Set());
  const { marquee, onMouseDown: onMarqueeDown, consumeDrag } = useMarquee({
    containerRef: listRef,
    rowCount: rows.length,
    onSelect: useCallback(
      (indices: number[], additive: boolean) => {
        const swept = indices.map((i) => rowKey(rows[i].kind, rows[i].id));
        setSelected(additive ? new Set([...marqueeBase.current, ...swept]) : new Set(swept));
      },
      [rows],
    ),
  });

  /** Click semantics: plain = replace, cmd/ctrl = toggle, shift = extend. */
  const selectAt = useCallback(
    (index: number, mods: { shift?: boolean; meta?: boolean }) => {
      // A marquee that ends over a row still emits a click on it; without this
      // the swept selection would collapse to that one row.
      if (consumeDrag()) return;
      setCursor(index);
      const key = rowKey(rows[index].kind, rows[index].id);
      if (mods.shift && anchorRef.current >= 0) {
        const [lo, hi] = [anchorRef.current, index].sort((a, b) => a - b);
        const range = rows.slice(lo, hi + 1).map((r) => rowKey(r.kind, r.id));
        setSelected(new Set(range));
        return;
      }
      anchorRef.current = index;
      if (mods.meta) toggleSel(key);
      else setSelected(new Set([key]));
    },
    [rows, consumeDrag],
  );

  /** Clicking empty space clears the selection (Cmd/Ctrl-drag keeps it). */
  const onListMouseDown = (e: React.MouseEvent) => {
    const el = e.target as HTMLElement;
    const onRow = el.closest("[data-row-index]");
    if (e.button === 0 && !onRow && !el.closest("button, a, input, th")) {
      marqueeBase.current = e.metaKey || e.ctrlKey ? new Set(selected) : new Set();
      if (!e.metaKey && !e.ctrlKey) {
        setSelected(new Set());
        setCursor(-1);
        anchorRef.current = -1;
      }
    }
    onMarqueeDown(e);
  };

  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  }

  // Password gate: a protected file needs ITS unlock token this session.
  const [pwPrompt, setPwPrompt] = useState<{ doc: DocFile; run: () => void } | null>(null);
  const [protecting, setProtecting] = useState<DocFile | null>(null);
  const requireUnlock = (d: DocFile, run: () => void) => {
    if (!d.isProtected || getDocUnlockToken(d.id)) run();
    else setPwPrompt({ doc: d, run });
  };

  const openFile = (d: DocFile) => requireUnlock(d, () => setPreview(d));

  const toggleProtection = (d: DocFile) => {
    if (!d.isProtected) {
      setProtecting(d); // opens the set-password modal
    } else {
      requireUnlock(d, () => {
        void updateDocFile(d.id, {
          isProtected: false,
          unlock: getDocUnlockToken(d.id) ?? undefined,
        }).then(refresh);
      });
    }
  };

  const downloadFile = (d: DocFile) =>
    requireUnlock(d, () => {
      const a = document.createElement("a");
      a.href = docFileUrl(d.id, getDocUnlockToken(d.id));
      a.download = d.name;
      a.click();
    });

  // ── Stars ───────────────────────────────────────────────────────────────
  const star = useMutation({
    mutationFn: ({
      kind,
      id,
      on,
    }: {
      kind: "file" | "folder";
      id: string;
      on: boolean;
    }) => setDocStar(kind, id, on),
    onSuccess: refresh,
  });
  const toggleStar = (kind: "file" | "folder", id: string, on: boolean) =>
    star.mutate({ kind, id, on });

  // ── Clipboard (cut / copy / paste) ──────────────────────────────────────
  //
  // Scoped to this tab rather than the OS clipboard: the payload is a set of
  // record ids, which means nothing to any other application.

  const cutKeys = useMemo(
    () =>
      clipboard?.mode === "cut"
        ? new Set(clipboard.items.map((i) => rowKey(i.kind, i.id)))
        : new Set<string>(),
    [clipboard],
  );

  const cutItems = (items: MoveItem[]) => {
    if (!items.length) return;
    setClipboard({ mode: "cut", items });
    setToast({ text: `${label(items)} ready to move. Paste in the destination folder.` });
  };

  const copyItems = (items: MoveItem[]) => {
    if (!items.length) return;
    setClipboard({ mode: "copy", items });
    setToast({ text: `${label(items)} copied.` });
  };

  const pasteInto = async (dest: string | null) => {
    if (!clipboard) return;
    const { mode, items } = clipboard;
    try {
      if (mode === "cut") {
        // Cut+paste is just a move, and moveItems already refreshes + toasts.
        await moveItems(items, dest);
      } else {
        const made = await copyDocItems(
          items.map((i) => ({ kind: i.kind, id: i.id })),
          dest,
        );
        refresh();
        setToast({ text: `Pasted ${made.length} item${made.length === 1 ? "" : "s"}.` });
      }
      // A cut is consumed by pasting; a copy stays on the clipboard so it can
      // be pasted into several places, like Finder.
      if (mode === "cut") setClipboard(null);
    } catch (err) {
      setToast({
        text: err instanceof ApiError ? err.message : "Could not paste those items.",
      });
    }
  };

  /** Paste needs somewhere real to land — the cross-folder views have no "here". */
  const canPaste = !!clipboard && scope.kind === "folder" && !searching;

  // ── Copy link ───────────────────────────────────────────────────────────
  const copyLink = async (kind: "file" | "folder", id: string) => {
    const url =
      kind === "file"
        ? `${window.location.origin}/docs?file=${id}`
        : `${window.location.origin}/docs/folder/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast({ text: "Link copied to clipboard." });
    } catch {
      setToast({ text: url });
    }
  };

  // ── Delete (to trash) ───────────────────────────────────────────────────
  const runDelete = async () => {
    if (!confirmDelete) return;
    const items: MoveItem[] =
      confirmDelete.kind === "bulk"
        ? confirmDelete.items
        : [
            {
              kind: confirmDelete.kind,
              id: confirmDelete.item.id,
              name: confirmDelete.item.name,
            },
          ];
    for (const it of items) {
      if (it.kind === "folder") await deleteDocFolder(it.id);
      else await deleteDocFile(it.id);
    }
    setSelected(new Set());
    setConfirmDelete(null);
    refresh();
    setToast({
      text:
        items.length === 1
          ? `“${items[0].name}” moved to trash.`
          : `${items.length} items moved to trash.`,
      undo: () => setTrashOpen(true),
    });
  };

  // ── Drag to move ────────────────────────────────────────────────────────
  /** Dragging a selected row drags the whole selection; otherwise just that row. */
  const dragPayload = (row: Row): MoveItem[] => {
    const key = rowKey(row.kind, row.id);
    if (selected.has(key) && selectedItems.length > 1) return selectedItems;
    return [{ kind: row.kind, id: row.id, name: row.name }];
  };

  const moveItems = useCallback(
    async (items: MoveItem[], destFolderId: string | null) => {
      const moved = items.filter((it) => !(it.kind === "folder" && it.id === destFolderId));
      if (!moved.length) return;
      try {
        for (const it of moved) {
          if (it.kind === "folder") await updateDocFolder(it.id, { parentId: destFolderId });
          else await updateDocFile(it.id, { folderId: destFolderId });
        }
        setSelected(new Set());
        refresh();
        setToast({
          text:
            moved.length === 1 ? `Moved “${moved[0].name}”.` : `Moved ${moved.length} items.`,
        });
      } catch (err) {
        setToast({
          text: err instanceof ApiError ? err.message : "Could not move those items.",
        });
      }
    },
    [refresh],
  );

  const readDrag = (e: React.DragEvent): MoveItem[] => {
    try {
      return JSON.parse(e.dataTransfer.getData(DRAG_TYPE)) as MoveItem[];
    } catch {
      return [];
    }
  };

  /** Drop handlers for anything that accepts a move (folder rows, breadcrumbs). */
  const dropTargetProps = (destId: string | null, targetKey: string) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      setDropRow(targetKey);
    },
    onDragLeave: () => setDropRow((k) => (k === targetKey ? null : k)),
    onDrop: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
      e.preventDefault();
      e.stopPropagation();
      setDropRow(null);
      void moveItems(readDrag(e), destId);
    },
  });

  /**
   * The thing that follows the cursor while dragging: the file's own icon and
   * name, or "N items" for a multi-selection. Built detached and thrown away
   * on the next tick — setDragImage only needs it laid out at that moment.
   */
  const setDragGhost = (e: React.DragEvent, row: Row, count: number) => {
    const ghost = document.createElement("div");
    ghost.className = "doc-drag-ghost";
    const icon = document.createElement("i");
    if (row.kind === "folder") {
      icon.className = "fa-solid fa-folder";
      icon.style.color = "var(--color-warning)";
    } else {
      const { icon: cls, color } = fileIcon(row.file.mime, row.file.name);
      icon.className = `fa-solid ${cls}`;
      icon.style.color = color;
    }
    const label = document.createElement("span");
    // textContent, never innerHTML — file names are user input.
    label.textContent = count > 1 ? `${count} items` : row.name;
    ghost.append(icon, label);
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 12, 16);
    setTimeout(() => ghost.remove(), 0);
  };

  /**
   * Marks something as a place a move-drag can begin. On an unselected row this
   * is the name+icon only, so the rest of the row stays marquee surface. On a
   * SELECTED row the whole row gets it — once things are selected, pressing
   * any part of them and dragging moves the lot, as in Finder.
   */
  const dragHandleProps = (row: Row) => ({
    "data-drag-handle": true,
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      // The name handle sits inside the row handle; without this the row's
      // handler would re-run on bubble and overwrite the drag image.
      e.stopPropagation();
      const payload = dragPayload(row);
      e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
      e.dataTransfer.effectAllowed = "move";
      setDragGhost(e, row, payload.length);
    },
    onDragEnd: () => setDropRow(null),
  });

  // ── Context menu ────────────────────────────────────────────────────────
  const openMenu = (e: React.MouseEvent, row: Row) => {
    e.preventDefault();
    const key = rowKey(row.kind, row.id);
    // Right-clicking outside the selection retargets it, like every file manager.
    const targets = selected.has(key) ? selectedItems : [{ kind: row.kind, id: row.id, name: row.name }];
    if (!selected.has(key)) setSelected(new Set([key]));
    setCursor(rows.findIndex((r) => rowKey(r.kind, r.id) === key));

    const many = targets.length > 1;
    const items: MenuItem[] = [];

    if (!many && row.kind === "file") {
      const d = row.file;
      items.push({ icon: "fa-eye", label: "Open", onClick: () => openFile(d) });
      if (canEditDoc(d.name)) {
        items.push({
          icon: "fa-pen-to-square",
          label: "Edit contents",
          onClick: () => requireUnlock(d, () => setEditing(d)),
        });
      }
      items.push({
        icon: "fa-download",
        label: "Download",
        onClick: () => downloadFile(d),
      });
      items.push({
        icon: "fa-clock-rotate-left",
        label: "Version history",
        onClick: () => requireUnlock(d, () => setHistory(d)),
      });
      items.push({
        icon: d.isProtected ? "fa-unlock" : "fa-lock",
        label: d.isProtected ? "Remove password" : "Protect with password",
        onClick: () => toggleProtection(d),
      });
    }
    if (!many && row.kind === "folder") {
      items.push({
        icon: "fa-folder-open",
        label: "Open",
        onClick: () => goToFolder(row.id),
      });
      items.push({
        icon: "fa-download",
        label: "Download as zip",
        onClick: () => window.open(folderZipUrl(row.id), "_blank"),
      });
    }
    if (many) {
      items.push({
        icon: "fa-download",
        label: `Download ${targets.length} items`,
        disabled: !targets.some((t) => t.kind === "file"),
        onClick: () =>
          void downloadDocsZip(targets.filter((t) => t.kind === "file").map((t) => t.id)),
      });
    }

    items.push({ icon: "fa-scissors", label: "Cut", onClick: () => cutItems(targets) });
    items.push({ icon: "fa-copy", label: "Copy", onClick: () => copyItems(targets) });
    // Pasting onto a folder drops the clipboard inside it, not beside it.
    if (!many && row.kind === "folder" && clipboard) {
      items.push({
        icon: "fa-paste",
        label: `Paste into “${row.name}”`,
        onClick: () => void pasteInto(row.id),
      });
    }

    items.push({
      icon: "fa-star",
      label: row.starred ? "Remove star" : "Star",
      onClick: () => toggleStar(row.kind, row.id, !row.starred),
    });
    if (!many) {
      items.push({
        icon: "fa-link",
        label: "Copy link",
        onClick: () => void copyLink(row.kind, row.id),
      });
      items.push({
        icon: "fa-pen",
        label: "Rename",
        onClick: () =>
          setRenaming(
            row.kind === "folder"
              ? { kind: "folder", item: row.folder }
              : { kind: "file", item: row.file },
          ),
      });
    }
    items.push({
      icon: "fa-trash",
      label: many ? `Delete ${targets.length} items` : "Delete",
      danger: true,
      onClick: () =>
        setConfirmDelete(
          many
            ? { kind: "bulk", items: targets }
            : row.kind === "folder"
              ? { kind: "folder", item: row.folder }
              : { kind: "file", item: row.file },
        ),
    });

    setMenu({ x: e.clientX, y: e.clientY, items });
  };

  /** Right-clicking empty space acts on the folder you're standing in. */
  const openBackgroundMenu = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-row-index]")) return;
    e.preventDefault();
    setSelected(new Set());
    const items: MenuItem[] = [];
    if (clipboard) {
      items.push({
        icon: "fa-paste",
        label: `Paste ${label(clipboard.items)}`,
        disabled: !canPaste,
        onClick: () => void pasteInto(folderId),
      });
    }
    items.push({
      icon: "fa-folder-plus",
      label: "New folder",
      disabled: scope.kind !== "folder",
      onClick: () => setNewFolderOpen(true),
    });
    items.push({
      icon: "fa-arrow-up-from-bracket",
      label: "Upload files",
      disabled: scope.kind !== "folder",
      onClick: () => fileInputRef.current?.click(),
    });
    items.push({
      icon: "fa-folder-tree",
      label: "Upload folder",
      disabled: scope.kind !== "folder",
      onClick: () => folderInputRef.current?.click(),
    });
    setMenu({ x: e.clientX, y: e.clientY, items });
  };

  // ── Keyboard navigation ─────────────────────────────────────────────────
  const openRow = useCallback(
    (row: Row) => (row.kind === "folder" ? goToFolder(row.id) : openFile(row.file)),
    // openFile depends only on stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goToFolder],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable);

      // "/" and Cmd/Ctrl+F jump to search from anywhere on the page.
      if (!typing && (e.key === "/" || ((e.metaKey || e.ctrlKey) && e.key === "f"))) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      if (typing) {
        if (e.key === "Escape") (el as HTMLInputElement).blur();
        return;
      }
      // Any modal owns the keyboard while it's open.
      if (
        preview ||
        editing ||
        history ||
        conflict ||
        renaming ||
        confirmDelete ||
        newFolderOpen ||
        trashOpen ||
        pwPrompt ||
        protecting ||
        menu
      ) {
        return;
      }
      if (!rows.length) return;

      switch (e.key) {
        case "ArrowDown":
        case "ArrowUp": {
          e.preventDefault();
          const delta = e.key === "ArrowDown" ? 1 : -1;
          const next = Math.max(0, Math.min(rows.length - 1, (cursor < 0 ? -delta : cursor) + delta));
          setCursor(next);
          if (e.shiftKey) {
            if (anchorRef.current < 0) anchorRef.current = cursor < 0 ? next : cursor;
            const [lo, hi] = [anchorRef.current, next].sort((a, b) => a - b);
            setSelected(new Set(rows.slice(lo, hi + 1).map((r) => rowKey(r.kind, r.id))));
          } else {
            anchorRef.current = next;
          }
          listRef.current
            ?.querySelector(`[data-row-index="${next}"]`)
            ?.scrollIntoView({ block: "nearest" });
          break;
        }
        case "Enter":
          if (cursor >= 0 && rows[cursor]) {
            e.preventDefault();
            openRow(rows[cursor]);
          }
          break;
        case " ":
          if (cursor >= 0 && rows[cursor]) {
            e.preventDefault();
            toggleSel(rowKey(rows[cursor].kind, rows[cursor].id));
          }
          break;
        case "a":
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            setSelected(new Set(allKeys));
          }
          break;
        case "x":
          if ((e.metaKey || e.ctrlKey) && selectedItems.length) {
            e.preventDefault();
            cutItems(selectedItems);
          }
          break;
        case "c":
          if ((e.metaKey || e.ctrlKey) && selectedItems.length) {
            e.preventDefault();
            copyItems(selectedItems);
          }
          break;
        case "v":
          if ((e.metaKey || e.ctrlKey) && canPaste) {
            e.preventDefault();
            void pasteInto(folderId);
          }
          break;
        case "Backspace":
        case "Delete":
          if (selectedItems.length) {
            e.preventDefault();
            setConfirmDelete({ kind: "bulk", items: selectedItems });
          }
          break;
        case "Escape":
          if (clipboard) setClipboard(null);
          else if (selected.size) setSelected(new Set());
          else if (query) setQueryDraft("");
          break;
        default:
          break;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [
    rows,
    cursor,
    selected,
    selectedItems,
    allKeys,
    query,
    openRow,
    clipboard,
    canPaste,
    folderId,
    preview,
    editing,
    history,
    conflict,
    renaming,
    confirmDelete,
    newFolderOpen,
    trashOpen,
    pwPrompt,
    protecting,
    menu,
  ]);

  const breadcrumb = browse.data?.breadcrumb ?? [];
  const scopeTitle =
    scope.kind === "recent"
      ? "Recent"
      : scope.kind === "starred"
        ? "Starred"
        : scope.kind === "mine"
          ? "My uploads"
          : "Documents";

  return (
    <div
      className="relative flex h-full overflow-hidden"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(DRAG_TYPE)) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragOver(false);
      }}
      onDrop={(e) => void onDrop(e)}
    >
      <DocsSidebar
        tree={tree.data ?? []}
        scope={scope}
        trashCount={trash.data?.length ?? 0}
        onScope={setScope}
        onOpenTrash={() => setTrashOpen(true)}
        onDropOn={(dest, e) => void moveItems(readDrag(e), dest)}
      />

      <div className="flex min-w-0 flex-1 flex-col px-8 py-6">
      {/* Title row matches every other page; the toolbar sits beneath it. */}
      <header className="mb-4">
        {/* Fixed height so the bar below doesn't jump when searching hides the view controls. */}
        <div className="flex min-h-[38px] flex-wrap items-center justify-between gap-3">
          <h1 className="page-title">{scopeTitle}</h1>

          <div className="flex shrink-0 items-center gap-2">
            {/* Cross-folder listings are always the path-annotated list — a grid
                would drop the "where is this?" context that makes them useful. */}
            {!listed && <SortMenu sort={sort} onSort={toggleSort} />}
            <div
              className={`flex overflow-hidden rounded-md border border-border ${listed ? "hidden" : ""}`}
            >
              <button
                onClick={() => setView("list")}
                className={`px-2.5 py-2 text-sm transition-colors ${
                  view === "list"
                    ? "bg-bg-elevated text-text-heading"
                    : "text-text-muted hover:text-text-heading"
                }`}
                title="List view"
              >
                <i className="fa-solid fa-list" />
              </button>
              <button
                onClick={() => setView("grid")}
                className={`px-2.5 py-2 text-sm transition-colors ${
                  view === "grid"
                    ? "bg-bg-elevated text-text-heading"
                    : "text-text-muted hover:text-text-heading"
                }`}
                title="Grid view"
              >
                <i className="fa-solid fa-table-cells-large" />
              </button>
            </div>
          </div>
        </div>

        <div className="relative mt-4">
          <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-text-muted" />
          <input
            ref={searchRef}
            value={queryDraft}
            onChange={(e) => setQueryDraft(e.target.value)}
            placeholder="Search all documents by name or contents. Typos are fine."
            aria-label="Search all documents"
            className="w-full rounded-token-md border border-border bg-bg-secondary py-2.5 pl-10 pr-10 text-sm text-text outline-none transition-colors focus:border-accent"
          />
          {queryDraft ? (
            <button
              onClick={() => setQueryDraft("")}
              title="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted hover:text-text-heading"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          ) : (
            <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border px-1 text-[0.6rem] text-text-muted">
              /
            </kbd>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
        {scope.kind === "folder" && !searching && (
          <nav className="flex min-w-0 items-center gap-1 text-sm">
            <button
              {...dropTargetProps(null, "crumb:root")}
              onClick={() => goToFolder(null)}
              className={`rounded px-1.5 py-0.5 transition-colors ${
                dropRow === "crumb:root"
                  ? "bg-accent-muted text-text-heading ring-1 ring-accent"
                  : folderId === null
                    ? "font-medium text-text-heading"
                    : "text-text-secondary hover:text-text-heading"
              }`}
            >
              <i className="fa-solid fa-house text-xs" />
            </button>
            {breadcrumb.map((c) => (
              <span key={c.id} className="flex min-w-0 items-center gap-1">
                <i className="fa-solid fa-chevron-right text-[0.55rem] text-text-muted" />
                <button
                  {...dropTargetProps(c.id, `crumb:${c.id}`)}
                  onClick={() => goToFolder(c.id)}
                  className={`truncate rounded px-1.5 py-0.5 transition-colors ${
                    dropRow === `crumb:${c.id}`
                      ? "bg-accent-muted text-text-heading ring-1 ring-accent"
                      : c.id === folderId
                        ? "font-medium text-text-heading"
                        : "text-text-secondary hover:text-text-heading"
                  }`}
                >
                  {c.name}
                </button>
              </span>
            ))}
          </nav>
        )}


        </div>
      </header>

      {/* Upload progress */}
      {uploads.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {uploads.map((u, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-2 rounded border border-border bg-bg-card px-2.5 py-1 text-[0.7rem] text-text-secondary"
            >
              {u.status === "up" ? (
                <i className="fa-solid fa-spinner fa-spin text-[0.6rem]" />
              ) : u.status === "ok" ? (
                <i className="fa-solid fa-circle-check text-[0.6rem] text-success" />
              ) : (
                <i className="fa-solid fa-circle-exclamation text-[0.6rem] text-danger" />
              )}
              <span className="max-w-[180px] truncate">{u.name}</span>
            </span>
          ))}
        </div>
      )}

        {/* Listing */}
        <div
          ref={listRef}
          onMouseDown={onListMouseDown}
          onContextMenu={openBackgroundMenu}
          className="min-h-0 flex-1 select-none overflow-y-auto rounded-token-lg border border-border bg-bg-card"
        >
          {isLoading ? (
            <SkeletonRows />
          ) : empty ? (
            searching ? (
              <EmptyState
                icon="fa-magnifying-glass"
                title="No matches"
                body={`Nothing in the hub matches “${query}” — not in a file name, and not in any indexed file's contents.`}
              />
            ) : scope.kind === "starred" ? (
              <EmptyState
                icon="fa-star"
                title="Nothing starred yet"
                body="Star a file or folder to pin it here — stars are private to you."
              />
            ) : scope.kind === "recent" ? (
              <EmptyState
                icon="fa-clock-rotate-left"
                title="Nothing here yet"
                body="Files you or your teammates change will show up here, newest first."
              />
            ) : scope.kind === "mine" ? (
              <EmptyState
                icon="fa-user"
                title="You haven't uploaded anything"
                body="Files you upload will be listed here."
                onUpload={() => fileInputRef.current?.click()}
              />
            ) : (
              <EmptyState
                icon="fa-folder-open"
                title="No documents yet"
                body="Drop files or folders anywhere on this page — everything is instantly indexed and searchable from Chat."
                onUpload={() => fileInputRef.current?.click()}
              />
            )
          ) : listed ? (
            <>
              {searching && results.data && !results.data.contentSearch && (
                <p className="border-b border-border-light px-4 py-2 text-[0.7rem] text-text-muted">
                  Showing file-name matches. Content search is unavailable right now.
                </p>
              )}
              <ResultsList
                items={listed}
                selected={selected}
                cursor={cursor}
                onSelect={selectAt}
                onOpen={(item) =>
                  item.kind === "folder" ? goToFolder(item.id) : openFile(item)
                }
                onNavigate={goToFolder}
                onContextMenu={(e, item) => {
                  const row = rows.find(
                    (r) => r.kind === item.kind && r.id === item.id,
                  );
                  if (row) openMenu(e, row);
                }}
              />
            </>
          ) : view === "grid" ? (
            <GridView
              rows={rows}
              selected={selected}
              cutKeys={cutKeys}
              cursor={cursor}
              dropRow={dropRow}
              onOpen={openRow}
              onSelect={selectAt}
              onContextMenu={openMenu}
              dragHandleProps={dragHandleProps}
              dropTargetProps={dropTargetProps}
            />
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-bg-card">
                <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                  <SortHeader label="Name" k="name" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Size" k="size" sort={sort} onSort={toggleSort} className="w-24" />
                  <SortHeader label="Added" k="added" sort={sort} onSort={toggleSort} className="w-32" />
                  <SortHeader
                    label="Last edited"
                    k="edited"
                    sort={sort}
                    onSort={toggleSort}
                    className="w-44"
                  />
                  {/* Its own column, so a file with an editor is no taller
                      than one without and every row lines up. */}
                  <th className="w-40 px-4 py-2.5 font-semibold uppercase tracking-wider">
                    Edited by
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const key = rowKey(row.kind, row.id);
                  const isDrop = dropRow === key;
                  // Cut items stay put but dim, so it's obvious they're pending.
                  const isCut = cutKeys.has(key);
                  const isSelected = selected.has(key);
                  const common = {
                    "data-row-index": i,
                    onClick: (e: React.MouseEvent) =>
                      selectAt(i, { shift: e.shiftKey, meta: e.metaKey || e.ctrlKey }),
                    onDoubleClick: () => openRow(row),
                    onContextMenu: (e: React.MouseEvent) => openMenu(e, row),
                    // A selected row is draggable in full, so grabbing any part
                    // of a multi-selection moves all of it.
                    ...(isSelected ? dragHandleProps(row) : {}),
                    // Zebra striping is the base layer; interactive states paint
                    // over it with an accent tint (--color-row-*), which stays
                    // legible on both stripes — a grey hover can't, since the
                    // light theme's neutrals sit within a few % of each other.
                    className: `group border-b border-border-light transition-colors ${
                      isSelected ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                    } ${isCut ? "opacity-50 " : ""}${
                      isDrop
                        ? "bg-[var(--color-row-selected)] ring-1 ring-inset ring-accent"
                        : cursor === i
                          ? "bg-[var(--color-row-selected)] ring-1 ring-inset ring-accent/40"
                          : isSelected
                            ? "bg-[var(--color-row-selected)]"
                            : `${i % 2 ? "bg-bg-secondary" : "bg-bg-card"} hover:bg-[var(--color-row-hover)]`
                    }`,
                  };

                  if (row.kind === "folder") {
                    const f = row.folder;
                    return (
                      <tr key={f.id} {...common} {...dropTargetProps(f.id, key)}>
                        <td className="px-4 py-2.5">
                          <span
                            {...dragHandleProps(row)}
                            title="Drag to move"
                            className="inline-flex cursor-grab items-center gap-2.5 rounded px-1 -mx-1 align-middle active:cursor-grabbing"
                          >
                            <i className="fa-solid fa-folder text-warning" />
                            <span className="text-text-heading">{f.name}</span>
                          </span>
                          {f.starred && (
                            <i
                              className="fa-solid fa-star ml-2 text-[10px] text-warning"
                              title="Starred"
                            />
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-text-muted">
                          {f.itemCount === 0
                            ? "Empty"
                            : `${f.itemCount} item${f.itemCount === 1 ? "" : "s"}`}
                        </td>
                        <td className="px-4 py-2.5 text-text-muted">
                          {new Date(f.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2.5 text-text-muted">
                          {f.itemCount > 0 ? fmtDateTime(f.updatedAt) : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-text-muted">—</td>
                      </tr>
                    );
                  }

                  const d = row.file;
                  const { icon, color } = fileIcon(d.mime, d.name);
                  return (
                    <tr key={d.id} {...common}>
                      <td className="px-4 py-2.5">
                        <span
                          {...dragHandleProps(row)}
                          title="Drag to move"
                          className="inline-flex cursor-grab items-center gap-2.5 rounded px-1 -mx-1 align-middle active:cursor-grabbing"
                        >
                          <i className={`fa-solid ${icon}`} style={{ color }} />
                          <span className="text-text">{d.name}</span>
                        </span>
                        {d.isProtected && (
                          <i
                            className="fa-solid fa-lock ml-2 text-[10px] text-warning"
                            title="Password protected"
                          />
                        )}
                        {d.starred && (
                          <i
                            className="fa-solid fa-star ml-2 text-[10px] text-warning"
                            title="Starred"
                          />
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-text-muted">{fmtSize(d.size)}</td>
                      <td className="px-4 py-2.5 text-text-muted">
                        {new Date(d.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2.5 text-text-secondary">
                        {fmtDateTime(d.updatedAt)}
                      </td>
                      <td className="truncate px-4 py-2.5 text-text-muted">
                        {d.lastEditedByName ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Clipboard indicator — a pending cut/copy is otherwise invisible once
          you navigate away from the folder you took it from. */}
      {clipboard && (
        <div
          className="absolute right-4 top-4 z-20 flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs shadow-lg"
          style={{ background: "var(--color-bg-elevated)" }}
        >
          <i
            className={`fa-solid ${clipboard.mode === "cut" ? "fa-scissors" : "fa-copy"} text-text-muted`}
          />
          <span className="text-text-secondary">
            {label(clipboard.items)} {clipboard.mode === "cut" ? "cut" : "copied"}
          </span>
          <button
            onClick={() => void pasteInto(folderId)}
            disabled={!canPaste}
            className="font-medium text-accent hover:underline disabled:text-text-muted disabled:no-underline"
            title={canPaste ? "Paste here" : "Open a folder to paste into"}
          >
            Paste here
          </button>
          <button
            onClick={() => setClipboard(null)}
            title="Clear clipboard (Esc)"
            className="text-text-muted hover:text-text-heading"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className="absolute bottom-4 left-4 z-30 flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm shadow-lg"
          style={{ background: "var(--color-bg-elevated)" }}
        >
          <span className="text-text-secondary">{toast.text}</span>
          {toast.undo && (
            <button
              onClick={() => {
                toast.undo?.();
                setToast(null);
              }}
              className="font-medium text-accent hover:underline"
            >
              Open trash
            </button>
          )}
        </div>
      )}

      {/* Drag overlay */}
      {dragOver && (
        <div
          className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-token-lg border-2 border-dashed"
          style={{
            borderColor: "var(--color-accent)",
            background: "var(--color-accent-muted)",
          }}
        >
          <div className="text-center">
            <i className="fa-solid fa-cloud-arrow-up mb-2 text-3xl text-accent" />
            <p className="text-sm font-medium text-text-heading">
              Drop files or folders to upload
            </p>
          </div>
        </div>
      )}

      {marquee && <MarqueeBox rect={marquee} />}

      {menu && <ContextMenu state={menu} onClose={() => setMenu(null)} />}

      {/* Hidden pickers */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []).map((file) => ({ file }));
          e.target.value = "";
          if (fs.length) startUpload(fs);
        }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        // @ts-expect-error non-standard, supported by every modern browser
        webkitdirectory=""
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []).map((file) => {
            const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
            const dir = rel?.includes("/") ? rel.slice(0, rel.lastIndexOf("/")) : undefined;
            return { file, relPath: dir };
          });
          e.target.value = "";
          if (fs.length) startUpload(fs);
        }}
      />

      {/* In-browser editor */}
      {editing && officeEditorFor(editing.name) && (
        <OfficeEditorModal
          editor={officeEditorFor(editing.name)!}
          docId={editing.id}
          name={editing.name}
          mime={editing.mime}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {editing && !officeEditorFor(editing.name) && (
        <DocEditorModal
          docId={editing.id}
          name={editing.name}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}

      {/* Preview modal */}
      {preview && (
        <DocPreviewModal
          name={preview.name}
          url={docFileUrl(preview.id, getDocUnlockToken(preview.id))}
          previewUrl={docPreviewUrl(preview.id, getDocUnlockToken(preview.id))}
          mime={preview.mime}
          size={preview.size}
          onClose={() => setPreview(null)}
        />
      )}

      {/* Trash */}
      {trashOpen && (
        <TrashModal onClose={() => setTrashOpen(false)} onChanged={refresh} />
      )}

      {/* Version history */}
      {pwPrompt && (
        <UnlockModal
          doc={pwPrompt.doc}
          onUnlocked={() => {
            const run = pwPrompt.run;
            setPwPrompt(null);
            run();
          }}
          onClose={() => setPwPrompt(null)}
        />
      )}
      {protecting && (
        <SetPasswordModal
          doc={protecting}
          onDone={() => {
            setProtecting(null);
            refresh();
          }}
          onClose={() => setProtecting(null)}
        />
      )}
      {history && (
        <HistoryModal doc={history} onClose={() => setHistory(null)} onRestored={refresh} />
      )}

      {/* Upload conflict */}
      {conflict && (
        <ConflictModal
          names={conflict.names}
          onResolve={resolveConflict}
          onCancel={() => setConflict(null)}
        />
      )}

      {/* New folder */}
      <NewFolderModal
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        onCreate={async (name) => {
          await createDocFolder(name, folderId);
          setNewFolderOpen(false);
          refresh();
        }}
      />

      {/* Rename */}
      <RenameModal
        state={renaming}
        onClose={() => setRenaming(null)}
        onRename={async (name) => {
          if (!renaming) return;
          if (renaming.kind === "folder") await updateDocFolder(renaming.item.id, { name });
          else await updateDocFile(renaming.item.id, { name });
          setRenaming(null);
          refresh();
        }}
      />

      {/* Delete → trash */}
      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={
          confirmDelete?.kind === "bulk"
            ? "Move to trash"
            : confirmDelete?.kind === "folder"
              ? "Move folder to trash"
              : "Move file to trash"
        }
        footer={
          <>
            <button
              className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button
              className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ background: "var(--color-danger)" }}
              onClick={() => void runDelete()}
            >
              Move to trash
            </button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          {confirmDelete?.kind === "bulk" ? (
            <>
              Move <strong className="text-text-heading">{confirmDelete.items.length} items</strong>
              {confirmDelete.items.some((i) => i.kind === "folder") &&
                " (folders include everything inside them)"}{" "}
              to the trash?
            </>
          ) : (
            <>
              Move <strong className="text-text-heading">“{confirmDelete?.item.name}”</strong>
              {confirmDelete?.kind === "folder" && " and everything inside it"} to the trash?
            </>
          )}{" "}
          You can restore it for 30 days. Until then it's hidden from the hub and the chat
          assistant won't answer from it.
        </p>
      </Modal>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Pieces
   ════════════════════════════════════════════════════════════════════════ */

/** One sort control for both views — the table headers stay in sync with it. */
function SortMenu({
  sort,
  onSort,
}: {
  sort: { key: SortKey; dir: SortDir };
  onSort: (k: SortKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const labels: Record<SortKey, string> = {
    name: "Name",
    size: "Size",
    added: "Date added",
    edited: "Last edited",
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Sort"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-2 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading"
      >
        <i className="fa-solid fa-arrow-down-short-wide text-xs" />
        <span className="hidden lg:inline">{labels[sort.key]}</span>
        <i className={`fa-solid fa-caret-${sort.dir === "asc" ? "up" : "down"} text-[0.6rem]`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-md border border-border bg-bg-card py-1 shadow-lg"
        >
          {(Object.keys(labels) as SortKey[]).map((k) => (
            <button
              key={k}
              role="menuitem"
              onClick={() => onSort(k)}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-bg-card-hover ${
                sort.key === k ? "text-text-heading" : "text-text-secondary"
              }`}
            >
              <span className="flex-1">{labels[k]}</span>
              {sort.key === k && (
                <i
                  className={`fa-solid fa-caret-${sort.dir === "asc" ? "up" : "down"} text-xs text-accent`}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SortHeader({
  label,
  k,
  sort,
  onSort,
  className = "",
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === k;
  return (
    <th className={`px-4 py-2.5 font-semibold ${className}`}>
      <button
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-text-heading ${
          active ? "text-text-heading" : ""
        }`}
      >
        {label}
        {active && <i className={`fa-solid fa-caret-${sort.dir === "asc" ? "up" : "down"}`} />}
      </button>
    </th>
  );
}

function GridView({
  rows,
  selected,
  cutKeys,
  cursor,
  dropRow,
  onOpen,
  onSelect,
  onContextMenu,
  dragHandleProps,
  dropTargetProps,
}: {
  rows: Row[];
  selected: Set<string>;
  cutKeys: Set<string>;
  cursor: number;
  dropRow: string | null;
  onOpen: (row: Row) => void;
  onSelect: (index: number, mods: { shift?: boolean; meta?: boolean }) => void;
  onContextMenu: (e: React.MouseEvent, row: Row) => void;
  dragHandleProps: (row: Row) => Record<string, unknown>;
  dropTargetProps: (destId: string | null, key: string) => Record<string, unknown>;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 p-4">
      {rows.map((row, i) => {
        const key = rowKey(row.kind, row.id);
        const isDrop = dropRow === key;
        const cls = `group relative flex cursor-pointer flex-col items-center rounded-token-lg border p-4 transition-colors ${
          cutKeys.has(key) ? "opacity-50 " : ""
        }${
          isDrop
            ? "border-accent bg-accent-muted"
            : cursor === i
              ? "border-accent bg-bg-card-hover"
              : selected.has(key)
                ? "border-accent bg-accent-muted"
                : "border-border bg-bg-card hover:border-accent"
        }`;
        const handlers = {
          "data-row-index": i,
          onClick: (e: React.MouseEvent) =>
            onSelect(i, { shift: e.shiftKey, meta: e.metaKey || e.ctrlKey }),
          onDoubleClick: () => onOpen(row),
          onContextMenu: (e: React.MouseEvent) => onContextMenu(e, row),
          ...dragHandleProps(row),
        };

        if (row.kind === "folder") {
          return (
            <div key={key} {...handlers} {...dropTargetProps(row.id, key)} className={cls}>
              {row.starred && (
                <i className="fa-solid fa-star absolute right-2 top-2 text-[10px] text-warning" />
              )}
              <i className="fa-solid fa-folder mb-2 text-3xl text-warning" />
              <span className="w-full truncate text-center text-xs text-text-heading">
                {row.name}
              </span>
              <span className="mt-0.5 text-[0.65rem] text-text-muted">
                {row.folder.itemCount === 0 ? "Empty" : `${row.folder.itemCount} items`}
              </span>
            </div>
          );
        }

        const d = row.file;
        const { icon, color } = fileIcon(d.mime, d.name);
        const isImg = d.mime.startsWith("image/");
        return (
          <div key={key} {...handlers} className={cls}>
            {row.starred && (
              <i className="fa-solid fa-star absolute right-2 top-2 z-10 text-[10px] text-warning" />
            )}
            {isImg && (!d.isProtected || getDocUnlockToken(d.id)) ? (
              <img
                src={docFileUrl(d.id, getDocUnlockToken(d.id))}
                alt={d.name}
                loading="lazy"
                draggable={false}
                className="mb-2 h-16 w-full rounded object-cover"
              />
            ) : d.isProtected ? (
              <i className="fa-solid fa-lock mb-2 text-3xl text-warning" />
            ) : (
              <i className={`fa-solid ${icon} mb-2 text-3xl`} style={{ color }} />
            )}
            <span className="w-full truncate text-center text-xs text-text">
              {d.isProtected && <i className="fa-solid fa-lock mr-1 text-[9px] text-warning" />}
              {d.name}
            </span>
            <span className="mt-0.5 text-[0.65rem] text-text-muted">{fmtSize(d.size)}</span>
          </div>
        );
      })}
    </div>
  );
}

function NewFolderModal({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => onCreate(name.trim()),
    onSuccess: () => setName(""),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New folder"
      footer={
        <>
          <button
            className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded-md px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-40"
            style={{ background: "var(--color-accent)", color: "var(--color-on-accent)" }}
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && name.trim() && create.mutate()}
        placeholder="Folder name"
        className="w-full rounded-md border border-border bg-bg-secondary px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </Modal>
  );
}

function RenameModal({
  state,
  onClose,
  onRename,
}: {
  state: { kind: "folder" | "file"; item: DocFolder | DocFile } | null;
  onClose: () => void;
  onRename: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  useEffect(() => {
    if (state) setName(state.item.name);
  }, [state]);
  const rename = useMutation({ mutationFn: () => onRename(name.trim()) });
  return (
    <Modal
      open={!!state}
      onClose={onClose}
      title={`Rename ${state?.kind ?? ""}`}
      footer={
        <>
          <button
            className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded-md px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-40"
            style={{ background: "var(--color-accent)", color: "var(--color-on-accent)" }}
            disabled={!name.trim() || rename.isPending}
            onClick={() => rename.mutate()}
          >
            {rename.isPending ? "Renaming…" : "Rename"}
          </button>
        </>
      }
    >
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && name.trim() && rename.mutate()}
        className="w-full rounded-md border border-border bg-bg-secondary px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </Modal>
  );
}

function ConflictModal({
  names,
  onResolve,
  onCancel,
}: {
  names: string[];
  onResolve: (policy: ConflictPolicy) => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open
      onClose={onCancel}
      title="Some files already exist"
      footer={
        <button
          className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
          onClick={onCancel}
        >
          Cancel
        </button>
      }
    >
      <p className="text-sm text-text-secondary">
        {names.length === 1 ? (
          <>
            A file named <strong className="text-text-heading">“{names[0]}”</strong> already exists
            here.
          </>
        ) : (
          <>
            <strong className="text-text-heading">{names.length} files</strong> already exist here:
            <span className="mt-1 block max-h-24 overflow-y-auto text-xs text-text-muted">
              {names.join(", ")}
            </span>
          </>
        )}
      </p>
      <div className="mt-4 flex flex-col gap-2">
        <ConflictChoice
          icon="fa-arrows-rotate"
          title="Replace"
          body="Overwrite the existing file (kept as a version you can restore)."
          onClick={() => onResolve("replace")}
        />
        <ConflictChoice
          icon="fa-clone"
          title="Keep both"
          body="Upload as a new copy with a numbered name."
          onClick={() => onResolve("keep")}
        />
        <ConflictChoice
          icon="fa-forward"
          title="Skip"
          body="Don't upload the conflicting files."
          onClick={() => onResolve("skip")}
        />
      </div>
    </Modal>
  );
}

function ConflictChoice({
  icon,
  title,
  body,
  onClick,
}: {
  icon: string;
  title: string;
  body: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-start gap-3 rounded-md border border-border p-3 text-left transition-colors hover:border-accent hover:bg-bg-card-hover"
    >
      <i className={`fa-solid ${icon} mt-0.5 text-accent`} />
      <span>
        <span className="block text-sm font-medium text-text-heading">{title}</span>
        <span className="block text-xs text-text-secondary">{body}</span>
      </span>
    </button>
  );
}

function HistoryModal({
  doc,
  onClose,
  onRestored,
}: {
  doc: DocFile;
  onClose: () => void;
  onRestored: () => void;
}) {
  const versions = useQuery({
    queryKey: ["doc-versions", doc.id],
    queryFn: () => listDocVersions(doc.id),
  });
  const restore = useMutation({
    mutationFn: (versionId: string) => restoreDocVersion(doc.id, versionId),
    onSuccess: () => {
      onRestored();
      void versions.refetch();
    },
  });

  return (
    <Modal open onClose={onClose} title={`Version history — ${doc.name}`}>
      {versions.isLoading ? (
        <div className="grid place-items-center py-10 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin" />
        </div>
      ) : !versions.data || versions.data.length === 0 ? (
        <p className="py-6 text-center text-sm text-text-muted">
          No earlier versions yet. Versions are saved automatically each time this file is edited or
          replaced.
        </p>
      ) : (
        <ul className="divide-y divide-border-light">
          {(versions.data as DocVersion[]).map((v) => (
            <li key={v.id} className="flex items-center gap-3 py-2.5">
              <i className="fa-solid fa-clock-rotate-left text-text-muted" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-text">{fmtDateTime(v.createdAt)}</div>
                <div className="text-xs text-text-muted">
                  {fmtSize(v.size)}
                  {v.editedByName ? ` · ${v.editedByName}` : ""}
                </div>
              </div>
              <a
                href={docVersionUrl(doc.id, v.id)}
                download
                className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-bg-elevated hover:text-text-heading"
                title="Download this version"
              >
                <i className="fa-solid fa-download text-xs" />
              </a>
              <button
                onClick={() => restore.mutate(v.id)}
                disabled={restore.isPending}
                className="rounded-md border border-border px-2.5 py-1 text-xs text-text-secondary transition-colors hover:border-accent hover:text-text-heading disabled:opacity-40"
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   UnlockModal — asks for a file's password and mints a session unlock token
   (cached per tab; see api.unlockDoc).
   ════════════════════════════════════════════════════════════════════════ */
function UnlockModal({
  doc,
  onUnlocked,
  onClose,
}: {
  doc: DocFile;
  onUnlocked: () => void;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await unlockDoc(doc.id, password);
      onUnlocked();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? "Incorrect password."
          : err instanceof ApiError
            ? err.message
            : "Could not unlock — try again.",
      );
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Protected document">
      <div className="mb-4 flex items-center gap-3 text-sm text-text-secondary">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-warning/15">
          <i className="fa-solid fa-lock text-warning" />
        </span>
        <span>
          Enter the password for{" "}
          <span className="font-semibold text-text-heading">{doc.name}</span> to
          continue. It stays unlocked in this tab for 30 minutes.
        </span>
      </div>
      <input
        type="password"
        autoFocus
        placeholder="Document password"
        className="w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && void submit()}
      />
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
        >
          Cancel
        </button>
        <button
          onClick={() => void submit()}
          disabled={!password || busy}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ color: "var(--color-on-accent)" }}
        >
          {busy ? "Checking…" : "Unlock"}
        </button>
      </div>
    </Modal>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SetPasswordModal — choose a password for one file (Excel-style protect).
   ════════════════════════════════════════════════════════════════════════ */
function SetPasswordModal({
  doc,
  onDone,
  onClose,
}: {
  doc: DocFile;
  onDone: () => void;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  const submit = async () => {
    if (busy) return;
    setError(null);
    if (password.length < 4) {
      setError("Password must be at least 4 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await updateDocFile(doc.id, { isProtected: true, password });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not protect this file.");
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Protect with password">
      <div className="mb-4 flex items-center gap-3 text-sm text-text-secondary">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-warning/15">
          <i className="fa-solid fa-lock text-warning" />
        </span>
        <span>
          Set a password for{" "}
          <span className="font-semibold text-text-heading">{doc.name}</span>.
          Anyone will need it to view, edit, or download the file — and the
          chat assistant won't answer from its contents.
        </span>
      </div>
      <input
        type="password"
        autoFocus
        placeholder="Password (min 4 characters)"
        className={`${inputCls} mb-2`}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <input
        type="password"
        placeholder="Confirm password"
        className={inputCls}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && void submit()}
      />
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
        >
          Cancel
        </button>
        <button
          onClick={() => void submit()}
          disabled={!password || !confirm || busy}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ color: "var(--color-on-accent)" }}
        >
          {busy ? "Protecting…" : "Protect file"}
        </button>
      </div>
    </Modal>
  );
}
